import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import { useTradingColors } from "../../Spot/tradingColorConfig";

const INTERVALS = [
  { label: "1m", value: "1" },
  { label: "3m", value: "3" },
  { label: "5m", value: "5" },
  { label: "15m", value: "15" },
  { label: "30m", value: "30" },
  { label: "1h", value: "60" },
  { label: "4h", value: "240" },
  { label: "1D", value: "1D" },
  { label: "1W", value: "1W" },
  { label: "1M", value: "1M" },
];

const normalizeDerivativeAsset = (a) => {
  const cCode = (a?.currencyCode || a?.currencycode || "").toUpperCase();
  const bCode = (a?.baseCurrency || a?.basecurrency || "USDT").toUpperCase();
  const pair = a?.assetPair || `${cCode}${bCode}_PERP`;
  const name = a?.assetPairName || `${cCode}${bCode} Perpetual`;

  return {
    currencyCode: cCode,
    baseCurrency: bCode,
    assetPair: pair,
    assetPairName: name,
    ltpValue: a?.ltpValue != null ? a.ltpValue : a?.lastTrade1 != null ? a.lastTrade1 : "",
    roc: a?.roc != null ? parseFloat(a.roc) : 0,
    volume: a?.volume || 0,
    currencyType: a?.currencyType,
    amountPrecision: a?.amountPrecision,
    pricePrecision: a?.pricePrecision,
  };
};

const FutureChartSlotHeader = ({
  currencycode,
  basecurrency,
  assetpair,
  assetPairName,
  interval = "60",
  onIntervalSelect,
  onSymbolSelect,
  isActive = false,
  btnColor: propBtnColor,
  onActivate,
  onClose,
  showClose = false,
}) => {
  const reduxBtnColor = useSelector((state) => state?.btncolor);
  const themeBtnColor = propBtnColor || reduxBtnColor || "#1976d2";
  const { upColor, downColor } = useTradingColors();
  const [showSymbolSearch, setShowSymbolSearch] = useState(false);
  const [showIntervalMenu, setShowIntervalMenu] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedBaseTab, setSelectedBaseTab] = useState("ALL");
  const [assets, setAssets] = useState([]);

  const searchDropdownRef = useRef(null);
  const symbolBtnRef = useRef(null);
  const intervalDropdownRef = useRef(null);
  const intervalBtnRef = useRef(null);

  // Load derivative assets from localStorage or CacheService
  const loadAssets = () => {
    try {
      const cached = localStorage.getItem("derivative_assets");
      if (cached) {
        const parsed = JSON.parse(cached);
        const values = Array.isArray(parsed) ? parsed : parsed?.Values;
        if (Array.isArray(values) && values.length) {
          const valid = values
            .map(normalizeDerivativeAsset)
            .filter((a) => a.currencyCode && a.assetPair);
          if (valid.length) {
            setAssets(valid);
            return;
          }
        }
      }
    } catch (e) {
      console.warn("FutureChartSlotHeader load assets error:", e);
    }

    // Fallback: Fetch from CacheService
    fetch("https://accounts.paybito.com/CacheService/api/getData?Name=DerivativeAssets")
      .then((res) => res.json())
      .then((data) => {
        if (data?.value) {
          const parsed = JSON.parse(data.value);
          localStorage.setItem("derivative_assets", data.value);
          const values = Array.isArray(parsed) ? parsed : parsed?.Values;
          if (Array.isArray(values)) {
            const valid = values
              .map(normalizeDerivativeAsset)
              .filter((a) => a.currencyCode && a.assetPair);
            setAssets(valid);
          }
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadAssets();
    const handleStorage = (e) => {
      if (!e || e.key === "derivative_assets") {
        loadAssets();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (showSymbolSearch) {
      loadAssets();
    }
  }, [showSymbolSearch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(e.target) &&
        symbolBtnRef.current &&
        !symbolBtnRef.current.contains(e.target)
      ) {
        setShowSymbolSearch(false);
      }
      if (
        intervalDropdownRef.current &&
        !intervalDropdownRef.current.contains(e.target) &&
        intervalBtnRef.current &&
        !intervalBtnRef.current.contains(e.target)
      ) {
        setShowIntervalMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Compute available base currencies for tabs
  const availableBaseTabs = useMemo(() => {
    const baseSet = new Set();
    assets.forEach((a) => {
      if (a.baseCurrency) baseSet.add(a.baseCurrency);
    });
    const priority = ["USDT", "USD", "BTC", "ETH"];
    const sortedBases = Array.from(baseSet).sort((a, b) => {
      const ai = priority.indexOf(a);
      const bi = priority.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    return ["ALL", ...sortedBases];
  }, [assets]);

  // Filter derivative assets by search query & base tab
  const filteredAssets = useMemo(() => {
    let list = assets;
    const q = searchText.trim().toUpperCase();

    if (!q) {
      if (selectedBaseTab !== "ALL") {
        list = list.filter((a) => a.baseCurrency === selectedBaseTab);
      }
      return list;
    }

    const cleanQ = q.replace(/[\/\-_ ]/g, "");
    return list.filter((a) => {
      const pairStr = `${a.currencyCode}/${a.baseCurrency}`;
      const pairJoined = `${a.currencyCode}${a.baseCurrency}`;
      const perpStr = a.assetPair?.toUpperCase() || "";
      const nameStr = a.assetPairName?.toUpperCase() || "";

      if (selectedBaseTab !== "ALL" && !q.includes("/") && !q.includes("-") && !q.includes(" ")) {
        if (a.baseCurrency !== selectedBaseTab) return false;
      }

      return (
        pairStr.includes(q) ||
        pairJoined.includes(cleanQ) ||
        perpStr.includes(cleanQ) ||
        nameStr.includes(q) ||
        a.currencyCode.includes(q) ||
        a.baseCurrency.includes(q)
      );
    });
  }, [assets, searchText, selectedBaseTab]);

  // Find active asset for price & 24h stats
  const currentAsset = useMemo(() => {
    return assets.find((a) => {
      if (assetpair && a.assetPair?.toUpperCase() === assetpair?.toUpperCase()) {
        return true;
      }
      return (
        a.currencyCode?.toUpperCase() === currencycode?.toUpperCase() &&
        a.baseCurrency?.toUpperCase() === basecurrency?.toUpperCase()
      );
    });
  }, [assets, assetpair, currencycode, basecurrency]);

  const ltp = currentAsset?.ltpValue || "";
  const roc = parseFloat(currentAsset?.roc || 0);
  const isPositive = roc >= 0;

  const currentIntervalLabel = useMemo(() => {
    const found = INTERVALS.find((i) => i.value === String(interval));
    return found ? found.label : interval;
  }, [interval]);

  const displayPairName = assetPairName || currentAsset?.assetPairName || `${currencycode}/${basecurrency}`;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "32px",
        minHeight: "32px",
        padding: "0 8px",
        backgroundColor: "var(--body-color-less2, #181a20)",
        borderBottom: "1px solid rgba(125, 125, 125, 0.15)",
        fontSize: "11px",
        userSelect: "none",
        position: "relative",
        zIndex: 50,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (onActivate) onActivate();
      }}
    >
      {/* ── LEFT: Symbol & Ticker Stats ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {/* Symbol Button */}
        <div style={{ position: "relative" }}>
          <button
            ref={symbolBtnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowSymbolSearch(!showSymbolSearch);
              setShowIntervalMenu(false);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 6px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 125, 125, 0.2)",
              backgroundColor: showSymbolSearch
                ? alpha(themeBtnColor, 0.12)
                : "rgba(255, 255, 255, 0.04)",
              color: "var(--text-color, #eaecef)",
              fontWeight: 700,
              fontSize: "11px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              outline: "none",
            }}
          >
            <span>{displayPairName}</span>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              style={{
                transform: showSymbolSearch ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease",
                opacity: 0.7,
              }}
            >
              <path
                d="M1 1L4 4L7 1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Symbol Search Dropdown Popup */}
          {showSymbolSearch && (
            <div
              ref={searchDropdownRef}
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 1500,
                width: "270px",
                maxHeight: "360px",
                backgroundColor: "var(--body-color, #1e2329)",
                border: "1px solid rgba(125, 125, 125, 0.25)",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                backdropFilter: "blur(8px)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Search Box */}
              <div
                style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid rgba(125, 125, 125, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "rgba(0,0,0,0.2)",
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" opacity="0.6">
                  <circle cx="5" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.2" />
                  <path d="M7.5 7.5L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search contract..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{
                    flex: 1,
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "var(--text-color, #fff)",
                    fontSize: "11px",
                  }}
                />
                {searchText && (
                  <span
                    onClick={() => setSearchText("")}
                    style={{
                      cursor: "pointer",
                      fontSize: "11px",
                      opacity: 0.6,
                      padding: "0 2px",
                      color: "var(--text-color, #fff)",
                    }}
                  >
                    ✕
                  </span>
                )}
              </div>

              {/* Base Currency Tabs */}
              {availableBaseTabs.length > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    borderBottom: "1px solid rgba(125, 125, 125, 0.12)",
                    overflowX: "auto",
                  }}
                >
                  {availableBaseTabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setSelectedBaseTab(tab)}
                      style={{
                        padding: "2px 6px",
                        fontSize: "10px",
                        fontWeight: 600,
                        borderRadius: "3px",
                        border: "none",
                        cursor: "pointer",
                        backgroundColor:
                          selectedBaseTab === tab
                            ? alpha(themeBtnColor, 0.2)
                            : "transparent",
                        color:
                          selectedBaseTab === tab
                            ? themeBtnColor
                            : "var(--text-color-less, #848e9c)",
                        transition: "all 0.12s ease",
                      }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              )}

              {/* Header row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.3fr 1fr 0.9fr",
                  padding: "4px 8px",
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "rgba(180, 185, 195, 0.7)",
                  borderBottom: "1px solid rgba(125, 125, 125, 0.1)",
                }}
              >
                <span>Pair</span>
                <span style={{ textAlign: "right" }}>Price</span>
                <span style={{ textAlign: "right" }}>24h%</span>
              </div>

              {/* Pairs list */}
              <div
                style={{
                  maxHeight: "240px",
                  overflowY: "auto",
                }}
              >
                {filteredAssets.length === 0 ? (
                  <div
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      color: "rgba(180, 185, 195, 0.6)",
                      fontSize: "11px",
                    }}
                  >
                    No matching pairs
                  </div>
                ) : (
                  filteredAssets.map((a) => {
                    const isSelected =
                      (assetpair && a.assetPair?.toUpperCase() === assetpair?.toUpperCase()) ||
                      (!assetpair &&
                        a.currencyCode === currencycode?.toUpperCase() &&
                        a.baseCurrency === basecurrency?.toUpperCase());
                    const aRoc = parseFloat(a.roc || 0);
                    const aPositive = aRoc >= 0;

                    return (
                      <div
                        key={a.assetPair}
                        onClick={() => {
                          if (onSymbolSelect) {
                            onSymbolSelect({
                              currencycode: a.currencyCode,
                              basecurrency: a.baseCurrency,
                              assetpair: a.assetPair,
                              assetPairName: a.assetPairName,
                            });
                          }
                          setShowSymbolSearch(false);
                        }}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.3fr 1fr 0.9fr",
                          padding: "6px 8px",
                          fontSize: "11px",
                          cursor: "pointer",
                          backgroundColor: isSelected
                            ? alpha(themeBtnColor, 0.12)
                            : "transparent",
                          transition: "background-color 0.12s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor =
                              "rgba(255, 255, 255, 0.05)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 600, color: "var(--text-color, #fff)" }}>
                            {a.currencyCode}
                            <span style={{ opacity: 0.6, fontSize: "9px" }}>/{a.baseCurrency}</span>
                          </span>
                          <span style={{ fontSize: "9px", opacity: 0.5 }}>Perpetual</span>
                        </div>
                        <span
                          style={{
                            textAlign: "right",
                            fontFamily: "monospace",
                            color: "var(--text-color, #fff)",
                            alignSelf: "center",
                          }}
                        >
                          {a.ltpValue ? Number(a.ltpValue).toLocaleString() : "-"}
                        </span>
                        <span
                          style={{
                            textAlign: "right",
                            fontWeight: 600,
                            color: aPositive ? upColor : downColor,
                            alignSelf: "center",
                          }}
                        >
                          {aPositive ? "+" : ""}
                          {aRoc.toFixed(2)}%
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Live LTP & 24h ROC */}
        {ltp !== "" && (
          <span
            style={{
              fontFamily: "monospace",
              fontWeight: 700,
              fontSize: "11px",
              color: isPositive ? upColor : downColor,
            }}
          >
            {Number(ltp).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
          </span>
        )}
        {ltp !== "" && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: 600,
              padding: "1px 4px",
              borderRadius: "3px",
              backgroundColor: isPositive
                ? alpha(upColor, 0.15)
                : alpha(downColor, 0.15),
              color: isPositive ? upColor : downColor,
            }}
          >
            {isPositive ? "+" : ""}
            {roc.toFixed(2)}%
          </span>
        )}
      </div>

      {/* ── RIGHT: Interval Selector & Close Button ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
        {/* Quick interval buttons */}
        {["1", "5", "15", "60", "240", "1D"].map((val) => {
          const item = INTERVALS.find((i) => i.value === val);
          const isSelected = String(interval) === val;
          return (
            <button
              key={val}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onIntervalSelect) onIntervalSelect(val);
              }}
              style={{
                padding: "2px 5px",
                fontSize: "10px",
                fontWeight: isSelected ? 700 : 500,
                borderRadius: "3px",
                border: "none",
                cursor: "pointer",
                backgroundColor: isSelected
                  ? alpha(themeBtnColor, 0.18)
                  : "transparent",
                color: isSelected ? themeBtnColor : "var(--text-color-less, #848e9c)",
                transition: "all 0.12s ease",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.color = "var(--text-color, #fff)";
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.color = "var(--text-color-less, #848e9c)";
              }}
            >
              {item?.label || val}
            </button>
          );
        })}

        {/* More intervals dropdown */}
        <div style={{ position: "relative" }}>
          <button
            ref={intervalBtnRef}
            type="button"
            title="More intervals"
            onClick={(e) => {
              e.stopPropagation();
              setShowIntervalMenu(!showIntervalMenu);
              setShowSymbolSearch(false);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
              padding: "2px 4px",
              borderRadius: "3px",
              border: "none",
              backgroundColor:
                !["1", "5", "15", "60", "240", "1D"].includes(String(interval))
                  ? alpha(themeBtnColor, 0.18)
                  : "transparent",
              color:
                !["1", "5", "15", "60", "240", "1D"].includes(String(interval))
                  ? themeBtnColor
                  : "var(--text-color-less, #848e9c)",
              cursor: "pointer",
              fontSize: "10px",
              fontWeight: 600,
            }}
          >
            <span>
              {!["1", "5", "15", "60", "240", "1D"].includes(String(interval))
                ? currentIntervalLabel
                : "•••"}
            </span>
          </button>

          {showIntervalMenu && (
            <div
              ref={intervalDropdownRef}
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                right: 0,
                zIndex: 1500,
                width: "90px",
                backgroundColor: "var(--body-color, #1e2329)",
                border: "1px solid rgba(125, 125, 125, 0.25)",
                borderRadius: "6px",
                boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                padding: "4px",
                gap: "2px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {INTERVALS.map((item) => {
                const isSelected = String(interval) === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      if (onIntervalSelect) onIntervalSelect(item.value);
                      setShowIntervalMenu(false);
                    }}
                    style={{
                      padding: "4px 8px",
                      fontSize: "11px",
                      fontWeight: isSelected ? 700 : 500,
                      borderRadius: "4px",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      backgroundColor: isSelected
                        ? alpha(themeBtnColor, 0.18)
                        : "transparent",
                      color: isSelected ? themeBtnColor : "var(--text-color, #eaecef)",
                      transition: "background-color 0.12s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Optional Close slot button */}
        {showClose && (
          <button
            type="button"
            title="Close this slot"
            onClick={(e) => {
              e.stopPropagation();
              if (onClose) onClose();
            }}
            style={{
              background: "none",
              border: "none",
              color: "rgba(180, 185, 195, 0.6)",
              cursor: "pointer",
              padding: "2px 4px",
              fontSize: "12px",
              lineHeight: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#f6465d";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "rgba(180, 185, 195, 0.6)";
            }}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default FutureChartSlotHeader;
